// hooks/useSavedChallenges.js
import { useState, useEffect, useCallback } from 'react';
import { firestore, auth } from '../firebase';
import { 
  collection, 
  addDoc, 
  deleteDoc, 
  doc, 
  query, 
  where, 
  getDocs, 
  onSnapshot,
  serverTimestamp 
} from 'firebase/firestore';

export const useSavedChallenges = () => {
  const [savedChallenges, setSavedChallenges] = useState([]);
  const [loading, setLoading] = useState(true);

  // Real-time listener for saved challenges
  useEffect(() => {
    const userId = auth.currentUser?.uid;
    
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      const q = query(
        collection(firestore, 'savedChallenges'),
        where('userId', '==', userId)
      );

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const challenges = snapshot.docs.map(doc => ({
            firestoreId: doc.id,
            ...doc.data()
          }));
          
          setSavedChallenges(challenges);
          setLoading(false);
        },
        (error) => {
          console.error('Error listening to saved challenges:', error);
          setLoading(false);
        }
      );

      return () => unsubscribe();
    } catch (error) {
      console.error('Error setting up listener:', error);
      setLoading(false);
    }
  }, []);

  // Save a challenge
  const saveChallenge = useCallback(async (challengeData) => {
    const userId = auth.currentUser?.uid;
    const userEmail = auth.currentUser?.email;
    
    if (!userId) {
      console.error('No user logged in');
      return null;
    }

    console.log('Attempting to save challenge:', challengeData);

    try {
      // Check if already saved
      const existing = savedChallenges.find(
        c => c.language === challengeData.language && c.title === challengeData.title
      );
      
      if (existing) {
        console.log('Challenge already saved');
        return existing.firestoreId;
      }

      const newSavedChallenge = {
        userId,
        userEmail,
        language: challengeData.language,
        title: challengeData.title,
        description: challengeData.description,
        difficulty: challengeData.difficulty,
        testCases: challengeData.testCases || [],
        remarks: challengeData.remarks || '',
        savedAt: serverTimestamp(),
      };

      // Only add challengeId if it exists
      if (challengeData.id) {
        newSavedChallenge.challengeId = challengeData.id;
      }

      console.log('Saving to Firestore:', newSavedChallenge);

      const docRef = await addDoc(
        collection(firestore, 'savedChallenges'), 
        newSavedChallenge
      );
      
      console.log('Successfully saved with ID:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('Error saving challenge:', error);
      console.error('Error details:', error.message);
      return null;
    }
  }, [savedChallenges]);

  // Remove a saved challenge
  const removeSavedChallenge = useCallback(async (language, title) => {
    const userId = auth.currentUser?.uid;
    
    if (!userId) {
      console.error('No user logged in');
      return false;
    }

    try {
      const challenge = savedChallenges.find(
        c => c.language === language && c.title === title
      );
      
      if (!challenge) {
        console.error('Challenge not found in saved list');
        return false;
      }

      await deleteDoc(doc(firestore, 'savedChallenges', challenge.firestoreId));
      return true;
    } catch (error) {
      console.error('Error removing saved challenge:', error);
      return false;
    }
  }, [savedChallenges]);

  // Check if a challenge is saved
  const isSaved = useCallback((language, title) => {
    return savedChallenges.some(
      c => c.language === language && c.title === title
    );
  }, [savedChallenges]);

  // Get saved challenge IDs as array (for backward compatibility)
  const getSavedIds = useCallback(() => {
    return savedChallenges
      .map(c => c.challengeId)
      .filter(id => id !== undefined && id !== null);
  }, [savedChallenges]);

  return {
    savedChallenges,
    loading,
    saveChallenge,
    removeSavedChallenge,
    isSaved,
    getSavedIds,
  };
};